import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import Label from '@/components/@IntranetLibrary/Label';
import LeaveDatePicker from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/LeaveDatePicker';
import BaseSelect from '@/components/@IntranetLibrary/Select';
import CloseIcon from '@/public/icons/close_black.svg';
import WorkHoursIcon from '@/public/icons/mypage/WorkHours.svg';
import TimeIcon from '@/public/icons/mypage/OvertimeTime.svg';
import { fetchApi } from '@/utils/api/customAxios';
import { useSnackbar } from '@/hooks/global-tools';
import { AxiosError } from 'axios';
import { useGetOvertimeHistory } from '@/hooks/apiHooks';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { useTranslation } from 'react-i18next';
import {
  approvalDialogActionsSx,
  approvalDialogPaperSx,
  getApprovalDialogActionButtonSx,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/approvalDialogStyles';

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

const getToday = () => formatDate(new Date());

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatDate(d);
};

const getNextDay = (dateStr: string) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return formatDate(d);
};

const parseWorkStartTime = (workingHours: string): string => {
  const match = workingHours.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : '09:00';
};

const parseWorkEndTime = (workingHours: string): string => {
  const match = workingHours.match(/~(\d{2}:\d{2})/);
  return match ? match[1] : '18:00';
};

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const generateTimeOptions = (
  fromMinutes: number,
  toMinutes: number,
): string[] => {
  const options: string[] = [];
  for (let t = fromMinutes; t <= toMinutes; t += 30) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return options;
};

const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
};

const toOptions = (times: string[]) =>
  times.map((t) => ({ label: t, value: t }));

type Props = {
  open: boolean;
  onClose: () => void;
  workingHours?: string;
  onSuccess?: () => void;
};

const OvertimeRequestModal = ({
  open,
  onClose,
  workingHours = '09:00~18:00',
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const { isMobilePath } = usePlatform();
  const actionButtonSx = getApprovalDialogActionButtonSx(isMobilePath);
  const workEndTime = parseWorkEndTime(workingHours);
  const workStartTime = parseWorkStartTime(workingHours);
  const minStartTime = minutesToTime(timeToMinutes(workEndTime) + 30);

  const [startDate, setStartDate] = useState(getToday);
  const [startTime, setStartTime] = useState(minStartTime);
  const [endDate, setEndDate] = useState(getToday);
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStartDate(getToday());
    setStartTime(minStartTime);
    setEndDate(getToday());
    setEndTime('');
    setReason('');
  }, [open, minStartTime]);

  const { showSuccess, showError } = useSnackbar();

  const now = new Date();
  const dow = now.getDay();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const weekStartStr = formatDate(weekStart);
  const weekEndStr = formatDate(weekEnd);

  const { data: weekOvertimeData, mutate } = useGetOvertimeHistory(
    weekStartStr,
    weekEndStr,
  );

  // 시작: 퇴근 시간 ~ 23:30
  const startTimeOptions = generateTimeOptions(
    timeToMinutes(minStartTime),
    23 * 60 + 30,
  );
  // 종료: 같은 날 → 시작+30분 ~ 23:30 / 익일 → 00:00 ~ 근무 시작 시간
  const endTimeOptions =
    startDate === endDate
      ? generateTimeOptions(timeToMinutes(startTime) + 30, 23 * 60 + 30)
      : generateTimeOptions(0, timeToMinutes(workStartTime));

  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
    const nextDay = getNextDay(newDate);
    if (endDate < newDate || endDate > nextDay) setEndDate(newDate);
    setEndTime('');
  };

  const handleStartTimeChange = (newTime: string) => {
    setStartTime(newTime);
    if (startDate === endDate && endTime) {
      const minEnd = timeToMinutes(newTime) + 30;
      if (timeToMinutes(endTime) < minEnd) setEndTime('');
    }
  };

  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
    setEndTime('');
  };

  const durationHours = (() => {
    if (!endTime) return null;
    const endExtra = endDate > startDate ? 24 * 60 : 0;
    const breakDeduction =
      timeToMinutes(startTime) < timeToMinutes(workEndTime) + 30 ? 30 : 0;
    const raw =
      timeToMinutes(endTime) +
      endExtra -
      timeToMinutes(startTime) -
      breakDeduction;
    if (raw <= 0) return null;
    const h = raw / 60;
    return h % 1 === 0 ? `${h}` : `${h}`;
  })();

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await fetchApi({
        method: 'POST',
        url: '/api/v1/passport/me/overtime',
        data: {
          startedAt: `${startDate} ${startTime}:00`,
          endedAt: `${endDate} ${endTime}:00`,
          reason,
        },
      });
      showSuccess({ message: t('MYPAGE.연장근무 신청이 완료되었습니다.') });
      onSuccess?.();
      onClose();
    } catch (e) {
      const message =
        e instanceof AxiosError
          ? e.response?.data?.message ??
            t('MYPAGE.연장근무 신청에 실패했습니다.')
          : t('MYPAGE.연장근무 신청에 실패했습니다.');
      showError({ message });
    } finally {
      setIsLoading(false);
      mutate();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobilePath}
      PaperProps={!isMobilePath ? { sx: approvalDialogPaperSx } : undefined}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: 500,
          backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
          borderBottom: '1px solid var(--dk-line, #E7E9EC)',
          lineHeight: '24px',
        }}
      >
        {t('MYPAGE.연장근무 신청')}
        <Button
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '12px',
            top: '8px',
            padding: '4px',
            minWidth: 'auto',
          }}
        >
          <CloseIcon width="24px" height="24px" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px 20px 40px', overflowY: 'auto' }}>
        {/* 안내 문구 */}
        <Box sx={{ mt: '20px', mb: '20px' }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--dk-text-secondary, #888D96)',
              lineHeight: '20px',
            }}
          >
            {t(
              'MYPAGE.근태 품의는 별도의 결재 없이 즉시 승인되며, 팀장을 포함한 소속 팀원에게 자동 공유됩니다.',
            )}
            <br />
            {t(
              'MYPAGE.원활한 업무 진행을 위해 팀장 및 동료에게 사전 공유를 권장 드립니다.',
            )}
          </Typography>
          <Typography
            sx={{
              marginTop: '24px',
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--dk-accent, #0066CC)',
              lineHeight: '20px',
            }}
          >
            {t('MYPAGE.적립시간은 발생일 기준 3개월 후 자동 소멸됩니다.')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--dk-text-secondary, #888D96)',
              lineHeight: '20px',
            }}
          >
            {t(
              'MYPAGE.실제 근무 종료 시점 상신을 원칙으로 합니다. 사전 신청은 불가하며, 당일 신청을 하지 못한 경우 익일까지는 상신 가능합니다.',
            )}
            <br />
            {t('MYPAGE.출퇴근 시 1층 스피드게이트 이용을 권장합니다.')}
            <br />
            {t(
              'MYPAGE.비상계단·지하주차장·비상엘리베이터 등 이용 시에도 반드시 인근 출입 리더기에 태깅해 주시기 바랍니다.',
            )}
            <br />
            {t(
              'MYPAGE.휴게시간(30분)은 자동 차감됩니다. 별도 추가 신청은 불가합니다.',
            )}
          </Typography>
          <Typography
            sx={{
              marginTop: '24px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--dk-danger, #F55060)',
              lineHeight: '20px',
            }}
          >
            {t(
              'MYPAGE.실제 근무한 시간만 신청하시기 바랍니다. 외출 복귀 후 연장근무를 시작한 경우, 시작시간을 복귀 시점으로 조정하셔야 합니다.',
            )}
            <br />
            {t(
              'MYPAGE.사실과 다르게 신청할 경우, 인사 규정에 따라 불이익이 발생할 수 있습니다.',
            )}
          </Typography>
        </Box>

        {/* 연장근무 현황 */}
        <Box sx={{ mb: '24px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--dk-text, #000)',
              mb: '8px',
            }}
          >
            {t('MYPAGE.연장근무 현황')}
          </Typography>
          <Box
            sx={{
              border: '1px solid var(--dk-line, #E7E9EC)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {[
              {
                label: t('MYPAGE.이번주 연장근무'),
                value: t('MYPAGE.{{used}} / {{limit}}시간', {
                  used: weekOvertimeData?.weeklyOvertimeHours ?? '-',
                  limit: weekOvertimeData?.weeklyLimitHours ?? '-',
                }),
              },
              {
                label: t('MYPAGE.이번달 연장근무'),
                value: t('MYPAGE.{{used}} / {{limit}}시간', {
                  used: weekOvertimeData?.monthlyOvertimeHours ?? '-',
                  limit: weekOvertimeData?.monthlyLimitHours ?? '-',
                }),
              },
            ].map((row, i, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  borderBottom:
                    i < arr.length - 1
                      ? '1px solid var(--dk-line, #E7E9EC)'
                      : 'none',
                }}
              >
                <Box
                  sx={{
                    width: '20%',
                    minWidth: 160,
                    padding: '8px',
                    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                    borderRight: '1px solid var(--dk-line, #E7E9EC)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    }}
                  >
                    {row.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '14px', color: 'var(--dk-text, #000)' }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* 섹션 구분 Divider — DialogContent 좌우 20px 패딩을 상쇄해 모달 끝까지 full-bleed */}
        <Box
          sx={{
            mx: '-20px',
            height: '8px',
            backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
            mb: '24px',
          }}
        />

        {/* 신청 시간 */}
        <Box sx={{ mb: '20px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--dk-text, #000)',
              mb: '12px',
            }}
          >
            {t('MYPAGE.신청 시간')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '24px', sm: '8px' },
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              mb: '12px',
            }}
          >
            {/* 시작일시 */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Label isRequired={true} title={t('MYPAGE.시작일시')} size={14} />
              <Box sx={{ display: 'flex', gap: { xs: '8px', sm: '6px' } }}>
                <LeaveDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  minDate={getYesterday()}
                  maxDate={getToday()}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <BaseSelect
                  value={startTime}
                  options={toOptions(startTimeOptions)}
                  onChange={(e) =>
                    handleStartTimeChange(e.target.value as string)
                  }
                  placeholder="HH:MM"
                  IconComponent={WorkHoursIcon}
                  sx={{
                    flex: 1,
                    '&&': { height: '44px' },
                    '& .MuiInputBase-input': {
                      minHeight: 0,
                      paddingRight: '40px !important',
                    },
                    '& .MuiSelect-icon': {
                      transform: 'none',
                      top: '50%',
                      marginTop: '-12px',
                    },
                  }}
                />
              </Box>
            </Box>

            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                height: '44px',
              }}
            >
              ~
            </Typography>

            {/* 종료일시 */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Label isRequired={true} title={t('MYPAGE.종료일시')} size={14} />
              <Box sx={{ display: 'flex', gap: { xs: '8px', sm: '6px' } }}>
                <LeaveDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                  maxDate={getNextDay(startDate)}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <BaseSelect
                  value={endTime}
                  options={toOptions(endTimeOptions)}
                  onChange={(e) => setEndTime(e.target.value as string)}
                  placeholder="HH:MM"
                  IconComponent={WorkHoursIcon}
                  sx={{
                    flex: 1,
                    '&&': { height: '44px' },
                    '& .MuiInputBase-input': {
                      minHeight: 0,
                      paddingRight: '40px !important',
                    },
                    '& .MuiSelect-icon': {
                      transform: 'none',
                      top: '50%',
                      marginTop: '-12px',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              padding: '12px',
              borderRadius: '8px',
              mb: '8px',
              backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{ fontSize: '14px', color: 'var(--dk-text-strong, #555)' }}
              >
                {t('MYPAGE.신청 시간')}
              </Typography>
              {durationHours !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <TimeIcon width={24} height={24} />
                  <Box
                    component="span"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--dk-danger, #F55060)',
                    }}
                  >
                    {t('MYPAGE.{{hours}}시간', { hours: durationHours })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--dk-text-secondary, #888D96)',
              lineHeight: '28px',
              fontWeight: '400',
            }}
          >
            {t(
              'MYPAGE.* 연장근무 가능 시간: 연장근무 시작시간 (휴게시간 30분 제외) ~ 익일 출근시간',
              { nsSeparator: false },
            )}
            <br />
            {t('MYPAGE.* 1주 최대 12시간, 월 30시간 이내 신청 가능')}
          </Typography>
        </Box>

        {/* 사유 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Label isRequired={true} title={t('MYPAGE.사유')} size={14} />
          <Box
            component="textarea"
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setReason(e.target.value)
            }
            placeholder={t('MYPAGE.연장근무 사유를 입력해 주세요.')}
            rows={4}
            sx={{
              width: '100%',
              padding: '16px',
              // 색을 지정하지 않으면 다크(color-scheme: dark)에서 브라우저 기본 검정 칸이 된다
              backgroundColor: 'var(--dk-bg, #FFFFFF)',
              color: 'var(--dk-text, #000000)',
              border: '1px solid var(--dk-line, #E7E9EC)',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              '&::placeholder': { color: 'var(--dk-text-dim, #C5C8CE)' },
              height: '150px',
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={approvalDialogActionsSx}>
        <Button
          variant="outlined"
          size="medium"
          onClick={onClose}
          sx={actionButtonSx}
        >
          {t('MYPAGE.닫기')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          sx={actionButtonSx}
          disabled={!reason.trim() || !endTime || isLoading}
          onClick={handleSubmit}
        >
          {t('MYPAGE.신청 완료')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OvertimeRequestModal;
